/**
 * Wagmi-backed replacement for the old `useWeb3React()` API from `@web3-react/core`.
 *
 * - **Not** the npm package `@web3-react/core` — this module is local to MPRO LAB.
 * - Import: `import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'`.
 * - New code should prefer `wagmi` hooks (`useAccount`, `useConnect`, …) directly.
 * - Real provider tree: `WagmiProvider` + `QueryClientProvider` in `Providers.tsx`.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Web3Provider } from '@ethersproject/providers'
import type { ExternalProvider } from '@ethersproject/providers'
import { disconnect, switchChain } from '@wagmi/core'
import { useAccount } from 'wagmi'
import { base, mainnet } from 'wagmi/chains'
import type { Connector as WagmiConnector } from 'wagmi'
import { wagmiConfig } from 'lib/wagmiConfig'
import type { Web3ReactConnector } from 'lib/web3ReactConnector'

function wrapConnector(
  connector: WagmiConnector | undefined,
  rawProvider: unknown,
): Web3ReactConnector | undefined {
  if (!connector) return undefined
  const target = connector as WagmiConnector
  return new Proxy(connector as object, {
    get(_t, prop) {
      if (prop === 'provider') return rawProvider
      if (prop === 'deactivate') {
        return async () => {
          await disconnect(wagmiConfig, { connector: target })
        }
      }
      if (prop === 'resetState') {
        return async () => {
          await disconnect(wagmiConfig, { connector: target })
        }
      }
      if (prop === 'activate') {
        return async (chainId?: number) => {
          if (chainId != null) {
            await switchChain(wagmiConfig, {
              chainId: chainId as typeof mainnet.id | typeof base.id,
              connector: target,
            })
          }
        }
      }
      const v = Reflect.get(target, prop, target)
      if (typeof v === 'function') return (v as (...a: unknown[]) => unknown).bind(target)
      return v
    },
  }) as Web3ReactConnector
}

/**
 * No-op wrapper. The app uses `WagmiProvider` in `Providers.tsx`; do not nest another web3 provider here.
 * Exported only for compatibility with outdated examples that wrapped the tree in `Web3ReactProvider`.
 */
export function Web3ReactProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

export function useWeb3React() {
  const { address, chainId, status, connector: wagmiConnector } = useAccount()
  const [provider, setProvider] = useState<Web3Provider | undefined>(undefined)
  const [rawProvider, setRawProvider] = useState<unknown>(undefined)

  useEffect(() => {
    if (status !== 'connected' || !wagmiConnector) {
      setProvider(undefined)
      setRawProvider(undefined)
      return undefined
    }
    let cancelled = false
    void (async () => {
      try {
        const raw = await wagmiConnector.getProvider()
        if (cancelled || !raw) return undefined
        setRawProvider(raw)
        setProvider(new Web3Provider(raw as ExternalProvider, 'any'))
        return undefined
      } catch {
        if (!cancelled) {
          setRawProvider(undefined)
          setProvider(undefined)
        }
        return undefined
      }
    })()
    return () => {
      cancelled = true
    }
  }, [status, wagmiConnector, chainId, address])

  const connector = useMemo(
    () => wrapConnector(wagmiConnector, rawProvider),
    [wagmiConnector, rawProvider],
  )

  return useMemo(
    () => ({
      account: address,
      chainId: chainId ?? undefined,
      isActive: status === 'connected',
      isActivating: status === 'connecting' || status === 'reconnecting',
      connector,
      provider,
      library: provider,
    }),
    [address, chainId, status, connector, provider],
  )
}
